// IDE — a full Eclipse Theia workspace on the app's repo, in the browser.
//
// The backend runs one Theia container per user on demand (shared/devOps/
// ide.js) and proxies it under /dev/ide/<id>/w/<token>/ (api/devIde.js); this
// panel opens the workspace, shows what it is doing while it starts, then
// hosts it in an iframe. Internal accounts get an edit workspace (commit +
// push from Theia's Source Control or terminal, then "Open PR" hands the
// branch to the Ship strip); org admins/owners get a read-only one.
//
// Rendered as the Dev Console's `ide` view and as /ide for org admins
// without the internal claim. Workspaces are ephemeral: an idle one is
// stopped after dev.ide.idleMinutes and uncommitted work goes with it.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { kitEnv } from '../../data/kitEnv.js';
import { getIdToken } from '../../data/authPlane';
import './IdePanel.css';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');
const TOKEN_KEY = (id) => `nv.ide.${id}`;
const PHASE_LABEL = {
  queued: 'Queued',
  launch: 'Starting the workspace task',
  init: 'Cloning the repository',
  clone: 'Cloning the repository',
  branch: 'Checking out the branch',
  kit: 'Checking out the kit reference',
  settings: 'Writing workspace settings',
  'init-done': 'Starting the IDE',
  ide: 'Starting the IDE',
  ready: 'Ready',
  failed: 'Failed',
  stopped: 'Stopped',
};

async function api(path, body, method) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: method || (body === undefined ? 'GET' : 'POST'),
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

const ago = (ms) => { if (!ms) return '—'; const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
const BRANCH_OK = /^(?!.*\.\.)(?!\/)(?!.*\/$)[\w./-]{1,120}$/;

function StatusTag({ w }) {
  const tone = w.status === 'ready' ? 'ok' : w.status === 'starting' ? 'info' : w.status === 'error' ? 'danger' : 'muted';
  return <span className={`ide-tag t-${tone}`}>{w.status === 'starting' ? (PHASE_LABEL[w.phase] || 'Starting') : w.status}</span>;
}

export default function IdePanel({ internal = false, onOpenSession = null, request = null }) {
  const [info, setInfo] = useState(null); // GET /dev/ide
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(internal ? 'edit' : 'view');
  const [branch, setBranch] = useState(request?.branch || 'main');
  const [current, setCurrent] = useState(null); // { id, token, ...workspace }
  const [prBusy, setPrBusy] = useState(false);
  const frameRef = useRef(null);
  const requestKey = request ? `${request.branch || ''}|${request.sessionId || ''}` : '';

  const load = useCallback(async () => {
    try { const r = await api('/dev/ide'); setInfo(r); setErr(null); return r; } catch (e) { setErr(e.message); return null; }
  }, []);

  const open = useCallback(async (opts = {}) => {
    setBusy(true);
    setErr(null);
    try {
      const w = await api('/dev/ide/open', { mode: opts.mode || mode, branch: opts.branch || branch, sessionId: opts.sessionId || request?.sessionId || null });
      sessionStorage.setItem(TOKEN_KEY(w.id), w.token);
      setCurrent(w);
      load();
      return w;
    } catch (e) { setErr(e.message); return null; } finally { setBusy(false); }
  }, [mode, branch, request, load]);

  // First load: resume a running workspace of mine whose token this tab
  // still holds; a "Open in IDE" request from a session opens straight away.
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await load();
      if (!alive || !r) return;
      if (!r.canEdit) setMode('view');
      if (request?.branch) {
        const w = await open({ mode: r.canEdit ? 'edit' : 'view', branch: request.branch, sessionId: request.sessionId });
        if (w) return;
      }
      const mine = (r.workspaces || []).find((w) => ['starting', 'ready'].includes(w.status) && sessionStorage.getItem(TOKEN_KEY(w.id)));
      if (mine) { setCurrent({ ...mine, token: sessionStorage.getItem(TOKEN_KEY(mine.id)) }); setBranch(mine.branch); setMode(mine.mode); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  // Follow the current workspace until it is ready (then every 30 s for
  // the idle clock and a stop from elsewhere).
  useEffect(() => {
    if (!current?.id) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const w = await api(`/dev/ide/${current.id}`);
        if (alive) setCurrent((c) => (c && c.id === w.id ? { ...c, ...w } : c));
      } catch (e) { if (alive) setErr(e.message); }
    };
    const id = setInterval(tick, current.status === 'ready' ? 30000 : 3000);
    return () => { alive = false; clearInterval(id); };
  }, [current?.id, current?.status]);

  const stop = useCallback(async (id) => {
    setBusy(true);
    try {
      await api(`/dev/ide/${id}/stop`, {});
      sessionStorage.removeItem(TOKEN_KEY(id));
      if (current?.id === id) setCurrent(null);
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [current, load]);

  const openPr = useCallback(async () => {
    if (!current) return;
    // eslint-disable-next-line no-alert
    const title = window.prompt('Pull request title', `IDE: ${current.branch}`);
    if (title === null) return;
    setPrBusy(true);
    try {
      const r = await api(`/dev/ide/${current.id}/pr`, { title });
      setCurrent((c) => (c ? { ...c, prUrl: r.prUrl, prNumber: r.prNumber, sessionId: r.sessionId } : c));
      if (onOpenSession) onOpenSession(r.sessionId);
    } catch (e) { setErr(e.message); } finally { setPrBusy(false); }
  }, [current, onOpenSession]);

  const frameSrc = useMemo(() => (current?.status === 'ready' && current.token ? `${API}/dev/ide/${current.id}/w/${current.token}/` : null), [current]);
  const idleLeft = useMemo(() => {
    if (!current || current.status !== 'ready' || !info?.idleMinutes) return null;
    return Math.max(0, Math.round(info.idleMinutes - (Date.now() - (current.lastActiveAt || current.readyAt || Date.now())) / 60000));
  }, [current, info]);
  const others = (info?.workspaces || []).filter((w) => ['starting', 'ready'].includes(w.status) && w.id !== current?.id);
  const canPr = current?.mode === 'edit' && info?.canEdit && current.branch && !['main', 'master'].includes(current.branch);
  const branchOk = BRANCH_OK.test(branch);

  if (info && !info.configured) {
    return <div className="cs-empty">The Dev Console repo is not configured on this instance (DEV_REPO) — there is nothing to open an IDE on.</div>;
  }
  if (info && !info.enabled) {
    return (
      <div className="ide-panel" data-nova-note="IDE workspaces are turned off on this instance">
        <div className="ide-head"><h2>IDE</h2></div>
        <div className="cs-empty">
          IDE workspaces are off. {internal ? <>Turn on <code>dev.ide.enabled</code> in Config (the Theia task family must be registered — see the enable-the-dev-ide runbook).</> : 'Ask an administrator to turn them on.'}
        </div>
      </div>
    );
  }

  return (
    <div className="ide-panel" data-nova-note={current ? `IDE workspace on ${current.branch} (${current.mode}), ${current.status}` : 'IDE: open a workspace on the repository'}>
      <div className="ide-head">
        <h2>IDE</h2>
        <span className="u-small u-muted">
          {info ? <>{info.repo}{info.canEdit ? ' · edit and push' : ' · read-only'} · idle stop after {info.idleMinutes} min</> : 'loading…'}
        </span>
        <span className="spacer" />
        {current && (
          <>
            <StatusTag w={current} />
            <span className="u-small u-muted">{current.mode} · <code>{current.branch}</code>{idleLeft != null ? ` · idle stop in ~${idleLeft} min` : ''}</span>
            {frameSrc && <a className="cs-btn cs-btn-ghost cs-btn-sm" href={frameSrc} target="_blank" rel="noopener noreferrer">Open in new tab</a>}
            {canPr && (
              current.prUrl
                ? <a className="cs-btn cs-btn-outline cs-btn-sm" href={current.prUrl} target="_blank" rel="noopener noreferrer">PR #{current.prNumber}</a>
                : <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={prBusy || current.status !== 'ready'} onClick={openPr} title="Push your commits from the IDE first; this files the pull request and hands it to the Ship strip">{prBusy ? 'Opening PR…' : 'Open PR'}</button>
            )}
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => stop(current.id)} data-agent-guard="stops the IDE workspace; uncommitted work is lost">Stop</button>
          </>
        )}
      </div>
      {err && <div className="cs-banner b-danger">{err}</div>}

      {!current && (
        <section className="cs-card ide-open">
          <h3>Open a workspace</h3>
          <p className="u-small u-muted">
            A full IDE (Eclipse Theia) on <code>{info?.repo || '…'}</code>{info?.canEdit ? ', with the kit checked out read-only under kit/ when this app is a layer. Commit and push from Source Control or the terminal, then Open PR.' : '. Browse files, history and diffs; nothing can be changed or pushed from a read-only workspace.'}
            {' '}Workspaces are ephemeral: stop, idle for {info?.idleMinutes ?? '…'} minutes, or {info?.maxHours ?? '…'} hours of age ends them and uncommitted work is lost.
          </p>
          <div className="ide-open-row">
            <label className="cs-field">
              <span>Mode</span>
              <select className="cs-input" value={mode} onChange={(e) => setMode(e.target.value)} disabled={!info?.canEdit}>
                {info?.canEdit && <option value="edit">Edit — commit and push</option>}
                <option value="view">Read-only</option>
              </select>
            </label>
            <label className="cs-field ide-branch">
              <span>Branch</span>
              <input className="cs-input" value={branch} onChange={(e) => setBranch(e.target.value.trim())} placeholder="main" spellCheck={false} />
              <span className="cs-help">{mode === 'edit' ? 'An existing branch, or a new name to branch from the default.' : 'Must exist on the remote.'}</span>
            </label>
            <button type="button" className="cs-btn cs-btn-primary" disabled={busy || !info || !branchOk} onClick={() => open()}>{busy ? 'Opening…' : 'Open workspace'}</button>
          </div>
        </section>
      )}

      {current && current.status === 'starting' && (
        <div className="ide-starting" role="status">
          <div className="cs-spinner" />
          <div>
            <div className="ide-starting-title">{PHASE_LABEL[current.phase] || 'Starting'}…</div>
            <div className="u-small u-muted">A cold start pulls the IDE image and clones the repository — usually one to two minutes. The workspace opens here when the IDE answers.</div>
          </div>
        </div>
      )}
      {current && current.status === 'error' && (
        <div className="cs-banner b-danger ide-failed">
          <span>The workspace failed to start: {current.error || 'unknown error'}</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { sessionStorage.removeItem(TOKEN_KEY(current.id)); setCurrent(null); }}>Dismiss</button>
        </div>
      )}
      {current && ['stopped', 'stopping'].includes(current.status) && (
        <div className="cs-banner b-info ide-failed">
          <span>This workspace was stopped{current.reason ? ` — ${current.reason}` : ''}.</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => { sessionStorage.removeItem(TOKEN_KEY(current.id)); setCurrent(null); }}>Open another</button>
        </div>
      )}
      {current && current.status === 'ready' && !current.token && (
        <div className="cs-banner b-info ide-failed">
          <span>This tab has no key for the running workspace.</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => open({ mode: current.mode, branch: current.branch })}>Reconnect</button>
        </div>
      )}
      {frameSrc && (
        <iframe
          ref={frameRef}
          className="ide-frame"
          title={`IDE workspace on ${current.branch}`}
          src={frameSrc}
          allow="clipboard-read; clipboard-write"
          data-nova-ignore
        />
      )}

      {others.length > 0 && (
        <section className="cs-card">
          <h3>{internal ? 'Running workspaces' : 'Your other workspaces'}</h3>
          <table className="cs-table">
            <thead><tr><th>Who</th><th>Branch</th><th>Mode</th><th>Status</th><th>Active</th><th /></tr></thead>
            <tbody>
              {others.map((w) => (
                <tr key={w.id}>
                  <td>{w.email || w.uid}</td>
                  <td><code>{w.branch}</code></td>
                  <td>{w.mode}</td>
                  <td><StatusTag w={w} /></td>
                  <td className="u-muted u-small">{ago(w.lastActiveAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {sessionStorage.getItem(TOKEN_KEY(w.id)) && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setCurrent({ ...w, token: sessionStorage.getItem(TOKEN_KEY(w.id)) })}>Show</button>}
                    <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => stop(w.id)} data-agent-guard="stops someone's IDE workspace">Stop</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
